// Маскот: где его не показывать, подсказка во всплывашке и личные настройки (спрятан, какую тренировку
// уже отметили облачком) в localStorage этого устройства.

import { useCallback, useMemo, useSyncExternalStore } from 'react'

import { readyIn } from '@/components/muscle-map/muscles'
import { pluralRu } from '@/lib/format'

import type { Character } from '../api'

/** Экраны со своей нижней панелью и сам «Персонаж»: там маскот мешает. */
const HIDDEN_ON = [/^\/character/, /^\/workout$/, /^\/ai\/chat/, /^\/templates\/[^/]+/, /^\/workouts\/new$/, /^\/workouts\/[^/]+\/edit$/]

export function mascotAllowedOn(pathname: string): boolean {
  return !HIDDEN_ON.some((re) => re.test(pathname))
}

const HIDDEN_KEY = 'telos.mascot.hidden'
const SEEN_KEY = 'telos.mascot.seen'
const ACHIEVEMENTS_KEY = 'telos.mascot.achievements'
const EVENT = 'telos-mascot'

function read(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    // приватный режим — настройка живёт до перезагрузки
  }
  window.dispatchEvent(new Event(EVENT))
}

function subscribe(onChange: () => void) {
  window.addEventListener(EVENT, onChange)
  window.addEventListener('storage', onChange)
  return () => {
    window.removeEventListener(EVENT, onChange)
    window.removeEventListener('storage', onChange)
  }
}

export function useMascotHidden(): [boolean, (hidden: boolean) => void] {
  const hidden = useSyncExternalStore(subscribe, () => read(HIDDEN_KEY) === '1')
  return [hidden, (value) => write(HIDDEN_KEY, value ? '1' : null)]
}

/** Последняя тренировка, за которую маскот уже показал облачко «+XP». */
export function useMascotSeen(): [string | null, (id: string) => void] {
  const seen = useSyncExternalStore(subscribe, () => read(SEEN_KEY))
  return [seen, (id) => write(SEEN_KEY, id)]
}

/** Достижения, которые уже показаны (маскотом или на странице персонажа). */
export function useSeenAchievements(): [Set<string>, (keys: string[]) => void] {
  const raw = useSyncExternalStore(subscribe, () => read(ACHIEVEMENTS_KEY))
  const seen = useMemo(() => {
    try {
      return new Set(raw ? (JSON.parse(raw) as string[]) : [])
    } catch {
      return new Set<string>()
    }
  }, [raw])
  const mark = useCallback(
    (keys: string[]) => write(ACHIEVEMENTS_KEY, JSON.stringify([...new Set([...seen, ...keys])])),
    [seen],
  )
  return [seen, mark]
}

const DAYS = ['день', 'дня', 'дней'] as const

/** Одна живая фраза маскота: первая тренировка → сегодняшняя → кто ещё восстанавливается и кто свежий →
 * забытая мышца → слабая сторона → до уровня. */
export function mascotTip(data: Character, label: (key: string) => string, today: Date = new Date()): string {
  const last = data.last_workout
  if (!last) return 'Запиши первую тренировку — за неё 50 XP и ещё по 5 за каждый рабочий подход.'
  if (new Date(last.started_at).toDateString() === today.toDateString()) {
    return `Сегодня +${last.xp} XP — отличная работа! Теперь мышцам нужен отдых.`
  }
  const recovery = Object.entries(data.recovery ?? {})
  const tired = recovery.filter(([, r]) => r.percent < 60).sort(([, a], [, b]) => a.percent - b.percent)[0]
  if (tired) {
    const [key, r] = tired
    // Свежие, которым за неделю досталось меньше всего, — им и стоит заняться.
    const fresh = recovery
      .filter(([, f]) => f.percent >= 90)
      .sort(([a], [b]) => (data.week[a]?.sets ?? 0) - (data.week[b]?.sets ?? 0))
      .slice(0, 2)
      .map(([k]) => label(k).toLowerCase())
    const when = r.ready_in_h ? `, свежие ${readyIn(r.ready_in_h)}` : ''
    return `${label(key)} — восстановление ${r.percent} %${when}.${fresh.length ? ` Сегодня свежие: ${fresh.join(', ')}.` : ''}`
  }
  const forgotten = Object.entries(data.week)
    .filter(([, w]) => w.sets === 0 && (w.days_since == null || w.days_since >= 7))
    .sort(([, a], [, b]) => (b.days_since ?? 999) - (a.days_since ?? 999))[0]
  if (forgotten) {
    const [key, w] = forgotten
    const when = w.days_since == null ? 'давно' : `${w.days_since} ${pluralRu(w.days_since, DAYS)}`
    return `${label(key)} без нагрузки ${when} — самое время их загрузить.`
  }
  const weakest = data.special
    .filter((s) => s.value != null)
    .sort((a, b) => (a.value ?? 0) - (b.value ?? 0))[0]
  if (weakest && weakest.value != null && weakest.value < 5) {
    return `Слабее всего ${weakest.name.toLowerCase()} — ${String(weakest.value).replace('.', ',')}. Подтянем?`
  }
  return `До уровня ${data.level + 1} осталось ${data.next_level_xp - data.xp} XP.`
}
